<script>
import ApiKeyMixin from '@lib/mixins/api-key'
import FormMixin from '@lib/mixins/modal-form'
import StickyButtonsMixin from '@lib/mixins/sticky-buttons'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'ApiKeyForm',
  mixins: [ApiKeyMixin, FormMixin, StickyButtonsMixin],
  props: {
    id: {
      type: String,
      default: null,
    },
    providerId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      progress: false,
      form: {
        providerId: '',
        name: '',
        scopes: [],
        active: true,
      },
      selectedScopes: [],
      selectedRoles: [],
      rules: {
        providerId: {
          required: true,
        },
        productId: {
          required: true,
          trigger: ['change'],
        },
        scopes: [
          {
            validator: (rule, value, callback) => {
              const emptyRoles = this.scopes
                .map((i, idx) => {
                  if (this.selectedScopes.includes(i.group) && !this.selectedRoles[idx].length) {
                    return i.group
                  }
                })
                .filter(i => i)
              if (!this.selectedScopes.length) {
                callback(new Error('At least one Scope is required'))
              } else if (emptyRoles.length) {
                const message = emptyRoles
                  .map(i => '"' + i + '"').join(', ')
                callback(new Error(`Scope${emptyRoles.length === 1 ? '' : 's'} for ${message} must be selected`))
              } else {
                callback()
              }
            },
          },
        ],
      },
      apiKeyScopes: 'scopes',
    }
  },
  computed: {
    ...mapState('provider', ['provider']),
    ...mapState('providers', [
      'providersList',
      'providerSearching',
    ]),
    ...mapState('apiKeys', ['apiKey', 'scopes']),
    isEdit() {
      return this.$route.meta.edit
    },
    title() {
      return (this.isEdit ? 'Edit' : 'Create') + ' API Key' + (this.isEdit ? ` ${this.form.id}` : '')
    },
    backRoute() {
      const { id, provider } = this
      const routeObj = {}
      if (this.isEdit) {
        routeObj.name = 'api-key'
        routeObj.params = {
          id,
          providerId: provider && provider.id,
        }
      } else {
        routeObj.name = 'api-keys'
      }
      return routeObj
    },
    products() {
      if (this.isEdit) {
        return this.provider.products
      } else {
        const item = this.providersList.find(item => item.value === this.form.providerId)
        return item && item.products
      }
    },
  },
  async created() {
    if (this.isEdit) {
      await Promise.all([
        this.getProvider({ id: this.providerId }),
        this.showApiKey(this.id),
        this.getScopes(),
      ])

      this.form = this.apiKey
      this.form.productId = this.apiKey.product && this.apiKey.product.id
      this.selectedScopes = this.preparedScopes.map(i => i.group)
      this.selectedRoles = this.scopes
        .map(i => {
          if (this.selectedScopes.includes(i.group)) {
            return this.preparedScopes.find(ps => ps.group === i.group).scopes
          } else return []
        })

      this.form.providerId = this.provider.id
    } else {
      this.getScopes()
    }

    this.loading = false

    this.$nextTick(() => {
      this.setScrollSetting()
    })
  },
  methods: {
    ...mapActions('provider', ['getProvider']),
    ...mapActions('providers', [
      'searchProviders',
    ]),
    ...mapActions('apiKeys', ['createApiKey', 'updateApiKey', 'showApiKey', 'getScopes']),
    checkDisabled(group) {
      return !this.selectedScopes.includes(group)
    },
    async onSubmit() {
      const formValid = await this.validate()

      if (!formValid) {
        return false
      }

      this.progress = true

      const { form, id } = this
      form.scopes = this.flattenScopes()

      let response
      if (this.isEdit) {
        response = await this.updateApiKey({ id, form })
      } else {
        response = await this.createApiKey(form)
      }

      const [error, success] = response

      if (error && error.violations) {
        error.violations = Object.keys(error.violations).reduce((acc, key) => {
          const index = key.split('.')[1]
          const fieldName = form.scopes[index]
          acc[fieldName] = ['The selected scope is invalid.']
          return acc
        }, {})
      }

      this.$notifier({
        response,
        successMsg: `API Key successfully ${this.isEdit ? 'edited' : 'created'}`,
      })

      if (!error) {
        this.$router.push({
          name: 'api-key',
          params: {
            id: success.id,
            providerId: success.provider.id,
            allow: true,
          },
        })
      }

      this.progress = false
    },
    validate() {
      return this.$refs.form.validate().catch(() => false)
    },
  },
}
</script>

<template>
  <main-layout
    :title="title"
    :loading="loading"
    :back="backRoute"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      :disabled="progress"
    >
      <el-form-item
        label="Provider Name"
        :class="$style.half"
        prop="providerId"
      >
        <el-input
          v-if="isEdit"
          disabled
          :value="provider.name"
        />

        <el-select
          v-else
          v-model="form.providerId"
          :remote-method="searchProviders"
          :loading="providerSearching"
          loading-text="Searching provider by name..."
          placeholder="Start typing provider name..."
          filterable
          remote
        >
          <el-option
            v-for="item in providersList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>

      <el-form-item
        label="API Key Name"
        prop="name"
        :class="$style.half"
      >
        <el-input
          v-model="form.name"
        />
      </el-form-item>

      <el-form-item
        label="Provider Product"
        prop="productId"
        :class="$style.half"
      >
        <el-select
          v-model="form.productId"
          :disabled="!products || !products.length"
        >
          <el-option
            v-for="product in products"
            :key="product.id"
            :value="product.product.id"
            :label="product.product.name"
          />
        </el-select>
      </el-form-item>

      <el-form-item
        label="Scopes"
        prop="scopes"
        :class="$style.scopes"
      >
        <div
          v-for="(scope, index) in scopes"
          :key="index"
          :class="$style.scope"
        >
          <el-checkbox
            v-model="selectedScopes"
            :label="scope.group"
            :class="$style.scopeCheckbox"
          />

          <el-select
            v-model="selectedRoles[index]"
            placeholder="Select scopes"
            multiple
            :disabled="checkDisabled(scope.group)"
            :class="$style.scopeSelect"
          >
            <el-option
              v-for="(s, i) in scope.scopes"
              :key="i"
              :value="s"
              :label="s"
            />
          </el-select>
        </div>
      </el-form-item>

      <el-form-item
        prop="active"
        data-test="switch"
      >
        <el-switch
          v-model="form.active"
        />
        <span
          :class="[
            $style.switchLabel,
            form.active && $style.switchLabelActive,
          ]"
        >
          {{ form.active ? 'Active' : 'Inactive' }}
        </span>
      </el-form-item>

      <div :class="$style.footer">
        <div :class="[isScrollEnabled && $style.controls, isScrollEnabled && stickyButtons && $style.showControls]">
          <el-button
            type="outline"
            :class="$style.button"
            class="el-button--wide"
            @click.stop="$router.push(backRoute)"
          >
            Cancel
          </el-button>

          <el-button
            type="primary"
            :class="$style.button"
            :loading="progress"
            class="el-button--wide"
            @click.stop="onSubmit"
          >
            Save
          </el-button>
        </div>
      </div>
    </el-form>
  </main-layout>
</template>


<style lang="scss" module>
.half {
  width: 640px;
}

.scopes {
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: rem(640px);

  :global {

    .el-form-item__content {
      flex-direction: column;
    }

    .el-form-item__error {
      font-size: rem(14px);
    }

    .el-checkbox {
      align-items: center;
      height: rem(40px);
    }

    .el-form-item__label {
      padding-bottom: rem(6px) !important;
    }
  }

  &:global(.is-error > .el-form-item__content) {
    margin-bottom: rem(24px);
  }
}

.scope {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: rem(0 0 20px);
}

.scopeCheckbox {
  width: rem(200px);
  min-width: rem(200px);
}

.scopeSelect {
  display: flex;
  align-items: center;
  margin-left: 1rem;
}

.switchLabel {
  margin-left: rem(14px);
  transition: color 0.3s;
}

.switchLabelActive {
  color: var(--color-success);
}


// Sticky Buttons
.footer {
  height: rem(40px);
}

.controls {
  position: fixed;
  bottom: 0;
  z-index: 1;
  width: calc(100% - 64px - 210px);
  padding: rem(32px 0);
  background: white;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 5px);
  transition: transform 0.3s;
  will-change: transform;
  transform: translate3d(0, 100%, 0);
}

.showControls {
  transform: translate3d(0, 0, 0);
}
</style>
